import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Minimize2, Sparkles, Headphones, Maximize2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { SupportChatWindow } from "./SupportChatWindow";
import { useMyActiveSession, useChatMessagesSubscription } from "@/hooks/useSupportChat";
import { useLocation } from "react-router-dom";
import { playNotificationBeep } from "@/lib/notificationSound";
import { useTranslation } from "react-i18next";

export const SupportChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const [showPulse, setShowPulse] = useState(true);
  const [tooltipDismissed, setTooltipDismissed] = useState(false);
  const [lastMessagePreview, setLastMessagePreview] = useState<string | null>(null);
  const { user } = useAuth();
  const location = useLocation();
  const { data: activeSession, refetch } = useMyActiveSession();
  const { t } = useTranslation();

  useChatMessagesSubscription(activeSession?.id, (newMsg) => {
    if (newMsg.sender_type === "admin") {
      setLastMessagePreview(newMsg.content?.slice(0, 60) || null);
      if (!isOpen || isMinimized) {
        setHasNewMessage(true);
        playNotificationBeep(0.3);
      }
    }
  });

  useEffect(() => {
    if (isOpen && !isMinimized) setHasNewMessage(false);
  }, [isOpen, isMinimized]);

  useEffect(() => {
    if (activeSession) setShowPulse(false);
  }, [activeSession]);

  const isHiddenRoute = location.pathname.startsWith("/admin") || location.pathname.startsWith("/event-lab");
  if (!user || isHiddenRoute) return null;

  const handleToggle = () => {
    if (isMinimized) {
      setIsMinimized(false);
      setHasNewMessage(false);
    } else {
      setIsOpen(!isOpen);
      setShowPulse(false);
    }
  };

  const handleMinimize = () => setIsMinimized(true);
  const handleClose = () => { setIsOpen(false); setIsMinimized(false); };

  const getStatusText = () => {
    if (!activeSession) return t("chatWidget.howCanWeHelp");
    if (activeSession.status === "active") return t("chatWidget.agentConnected");
    if (activeSession.status === "waiting") return t("chatWidget.waitingAgent");
    return t("chatWidget.inConversation");
  };

  const getStatusColor = () => {
    if (!activeSession) return "bg-green-500";
    if (activeSession.status === "active") return "bg-green-500";
    if (activeSession.status === "waiting") return "bg-amber-500 animate-pulse";
    return "bg-blue-500";
  };

  const showTooltip = showPulse && !activeSession && !tooltipDismissed;

  return (
    <>
      {/* Floating Chat Bubble */}
      <AnimatePresence>
        {!isOpen && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            className="fixed bottom-4 right-4 z-50 safe-area-bottom"
          >
            <AnimatePresence>
              {showTooltip && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="absolute right-[4.5rem] bottom-2 bg-card border border-border rounded-lg px-3 py-2 shadow-lg whitespace-nowrap flex items-center gap-2"
                >
                  <p className="text-sm font-medium flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-primary" />
                    {t("chatWidget.needHelp")}
                  </p>
                  <button
                    onClick={(e) => { e.stopPropagation(); setTooltipDismissed(true); }}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={t("chatWidget.close")}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            <Button
              onClick={handleToggle}
              className="h-14 w-14 sm:h-14 sm:w-14 rounded-full shadow-lg bg-gradient-to-br from-primary to-coral hover:from-primary/90 hover:to-coral/90 text-white relative group"
              size="icon"
              aria-label={t("chatWidget.openChat")}
              style={{ boxShadow: "0 4px 20px hsla(340, 82%, 52%, 0.3), 0 0 40px hsla(16, 100%, 66%, 0.15)" }}
            >
              <motion.div
                animate={showPulse ? { scale: [1, 1.2, 1] } : {}}
                transition={{ repeat: Infinity, duration: 2 }}
              >
                <MessageCircle className="h-6 w-6" />
              </motion.div>
              
              {(hasNewMessage || activeSession?.status === "waiting") && (
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="absolute -top-1 -right-1">
                  <Badge className="h-5 w-5 p-0 flex items-center justify-center bg-destructive text-destructive-foreground text-xs animate-pulse">
                    {hasNewMessage ? "!" : "•"}
                  </Badge>
                </motion.div>
              )}

              {activeSession && !hasNewMessage && (
                <div className={`absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full border-2 border-background ${getStatusColor()}`} />
              )}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.9 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed bottom-4 right-4 z-50 w-[calc(100vw-2rem)] sm:w-[400px] safe-area-bottom"
          >
            <div className="bg-card border border-border rounded-xl shadow-2xl overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-primary/10 via-primary/5 to-coral/10 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-coral flex items-center justify-center">
                      <Headphones className="h-5 w-5 text-white" />
                    </div>
                    <div className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-card ${getStatusColor()}`} />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold">{t("chatWidget.supportTitle")}</h3>
                    <p className="text-xs text-muted-foreground">{getStatusText()}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-white/10" onClick={handleMinimize} aria-label={t("chatWidget.minimize")}>
                    <Minimize2 className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-white/10" onClick={handleClose} aria-label={t("chatWidget.close")}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {isMinimized ? (
                <button
                  onClick={() => { setIsMinimized(false); setHasNewMessage(false); }}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-secondary/30 transition-colors"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {hasNewMessage && <span className="w-2 h-2 rounded-full bg-primary animate-pulse flex-shrink-0" />}
                    <p className="text-xs text-muted-foreground truncate">
                      {lastMessagePreview || t("chatWidget.resumeConversation")}
                    </p>
                  </div>
                  <Maximize2 className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                </button>
              ) : (
                <SupportChatWindow 
                  session={activeSession}
                  onSessionCreated={() => refetch()}
                  onEscalateToTicket={() => {}}
                />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
